import { useState, useRef } from "react";
import { CROSSWORD } from "@/data/exercises";

export default function Crossword({ onComplete }) {
  const { rows, cols, letters, cells, starts, clues } = CROSSWORD;
  const [values, setValues] = useState({});
  const [selected, setSelected] = useState(null);
  const wrong = useRef(0);
  const start = useRef(Date.now());
  const done = useRef(false);

  const activeKeys = Object.keys(cells);

  const checkDone = (vals) => activeKeys.every((k) => vals[k] === cells[k]);

  const pick = (letter) => {
    if (!selected) return;
    const v = { ...values, [selected]: letter };
    setValues(v);
    if (letter !== cells[selected]) wrong.current += 1;
    if (checkDone(v) && !done.current) {
      done.current = true;
      const duration = Math.round((Date.now() - start.current) / 1000);
      const acc = Math.max(40, 100 - wrong.current * 8);
      setTimeout(() => onComplete({ score: acc, accuracy: acc, duration }), 500);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto text-center" data-testid="crossword-game">
      <p className="text-2xl font-patient font-bold text-gray-700 mb-6">Mots croisés</p>

      <div className="inline-block mb-6">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex">
            {Array.from({ length: cols }).map((_, c) => {
              const key = `${r}-${c}`;
              const active = activeKeys.includes(key);
              if (!active) return <div key={c} className="w-14 h-14 m-0.5" />;
              const sel = selected === key;
              const val = values[key];
              const correct = val && val === cells[key];
              const bad = val && val !== cells[key];
              return (
                <button
                  key={c}
                  data-testid={`crossword-cell-${key}`}
                  onClick={() => setSelected(key)}
                  className={`relative w-14 h-14 m-0.5 rounded-lg border-4 font-patient font-black text-2xl flex items-center justify-center transition-colors duration-150 ${
                    sel
                      ? "border-amber-400 bg-amber-100"
                      : correct
                      ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                      : bad
                      ? "border-red-300 bg-red-50 text-red-600"
                      : "border-emerald-700/20 bg-white text-gray-800"
                  }`}
                >
                  {starts[key] && (
                    <span className="absolute top-0.5 left-1 text-xs font-bold text-gray-400">{starts[key]}</span>
                  )}
                  {val || ""}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="text-left max-w-md mx-auto mb-6 space-y-1">
        {clues.map((cl, i) => (
          <p key={i} className="font-care text-gray-600">
            <span className="font-bold text-emerald-700">{cl.num} {cl.dir} :</span> {cl.text}
          </p>
        ))}
      </div>

      <div className="flex justify-center gap-2 flex-wrap">
        {letters.map((l) => (
          <button
            key={l}
            onClick={() => pick(l)}
            data-testid={`crossword-letter-${l}`}
            className="w-12 h-12 rounded-xl bg-[#F8E4C7] border-4 border-emerald-700/20 font-patient font-black text-xl text-gray-800 active:scale-90 transition-transform duration-150"
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}
